package com.fantasytenis.web.components.bracket

import androidx.compose.runtime.*
import com.fantasytenis.shared.domain.bracket.entity.BracketMatchEntity
import com.fantasytenis.shared.domain.bracket.entity.BracketRoundEntity
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.web.hooks.*
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderBottom
import com.fantasytenis.web.theme.borderTop
import com.fantasytenis.web.theme.textTransform
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Display mode for the bracket tree.
 */
enum class BracketDisplayMode {
    /** Show all matches expanded (default for small draws) */
    FULL,
    /** Show early rounds collapsed (for 64/128 draws) - DEPRECATED, use CANVAS */
    COLLAPSED_EARLY_ROUNDS,
    /** Infinite canvas mode with pan/zoom (recommended for 64/128 draws) */
    CANVAS
}

/**
 * Main container for the bracket tree visualization.
 *
 * This is a scrollable container that displays the full bracket
 * with matches connected by lines showing the tournament progression.
 *
 * For large draws (64/128), it uses InfiniteCanvasBracket which provides
 * a Figma-style pan/zoom experience for better navigation of the full bracket.
 */
@Composable
fun BracketTreeContainer(
    rounds: List<BracketRoundEntity>,
    drawSize: Int,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit,
    modifier: StyleScope.() -> Unit = {},
    displayMode: BracketDisplayMode = if (drawSize >= 64) BracketDisplayMode.CANVAS else BracketDisplayMode.FULL,
    /**
     * All rounds including early rounds (R128/R64) for pick advancement calculation.
     * When displaying filtered rounds (e.g., R32+ only), pass the full rounds here
     * so PickAdvancementCalculator can trace feeder matches correctly.
     * If null, uses `rounds` for pick calculation.
     */
    allRoundsForPickCalculation: List<BracketRoundEntity>? = null
) {
    // Use allRoundsForPickCalculation if provided, otherwise fall back to rounds
    val roundsForPicks = allRoundsForPickCalculation ?: rounds

    // For large draws (64/128), use the infinite canvas with pan/zoom
    if (displayMode == BracketDisplayMode.CANVAS && drawSize >= 64) {
        InfiniteCanvasBracket(
            rounds = rounds,
            drawSize = drawSize,
            picks = picks,
            isViewOnly = isViewOnly,
            onSelectWinner = onSelectWinner,
            onShowMatchDetails = onShowMatchDetails,
            allRoundsForPickCalculation = allRoundsForPickCalculation
        )
        return
    }

    // Standard full bracket tree view
    // Calculate layout
    val layout = remember(rounds, drawSize) {
        BracketLayoutCalculator.calculateLayout(rounds, drawSize, useCompactHeight = drawSize >= 64)
    }

    // Create a map for quick match lookup
    val matchMap = remember(rounds) {
        rounds.flatMap { it.matches }.associateBy { it.id }
    }

    // Track hovered match for connector highlighting
    var hoveredMatchId by remember { mutableStateOf<String?>(null) }

    // Scroll container
    Div({
        classes("bracket-tree-container")
        style {
            width(100.percent)
            height(100.percent)
            overflow("auto")
            backgroundColor(Colors.Background)
            property("-webkit-overflow-scrolling", "touch")
            modifier()
        }
    }) {
        // Inner container with actual bracket size
        Div({
            classes("bracket-tree-inner")
            style {
                property("width", "${layout.totalWidth.toInt()}px")
                property("height", "${layout.totalHeight.toInt()}px")
                position(Position.Relative)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
            }
        }) {
            // SVG layer for connector lines (behind matches)
            BracketConnectorSvg(
                connectors = layout.connectors,
                totalWidth = layout.totalWidth,
                totalHeight = layout.totalHeight,
                highlightedMatchId = hoveredMatchId
            )

            // Match nodes layer (on top of connectors)
            layout.positions.forEach { position ->
                val match = matchMap[position.matchId]
                if (match != null) {
                    // Compute effective display players based on picks
                    // Use roundsForPicks (all rounds) for proper feeder match tracing
                    val (p1Display, p2Display) = PickAdvancementCalculator.getMatchDisplayPlayers(
                        match = match,
                        picks = picks,
                        allRounds = roundsForPicks
                    )

                    // Position the match node absolutely
                    Div({
                        style {
                            property("position", "absolute")
                            property("left", "${position.x.toInt()}px")
                            property("top", "${position.y.toInt()}px")
                            property("z-index", "1")
                        }
                        onMouseEnter { hoveredMatchId = position.matchId }
                        onMouseLeave { hoveredMatchId = null }
                    }) {
                        BracketMatchNode(
                            match = match,
                            selectedWinnerId = picks[match.player1.id],
                            isViewOnly = isViewOnly,
                            variant = getVariantForDrawSize(drawSize, position.round),
                            width = position.width,
                            height = position.height,
                            onSelectWinner = { winnerId ->
                                onSelectWinner(match.player1.id, winnerId)
                            },
                            onShowDetails = { onShowMatchDetails(match) },
                            // Pass computed display players for pick advancement
                            player1Display = PlayerDisplayData(
                                name = p1Display.name,
                                seed = p1Display.seed,
                                country = p1Display.country,
                                playerId = p1Display.playerId,
                                isFromPick = p1Display.isFromPick,
                                potentialPlayers = p1Display.potentialPlayers
                            ),
                            player2Display = PlayerDisplayData(
                                name = p2Display.name,
                                seed = p2Display.seed,
                                country = p2Display.country,
                                playerId = p2Display.playerId,
                                isFromPick = p2Display.isFromPick,
                                potentialPlayers = p2Display.potentialPlayers
                            )
                        )
                    }
                }
            }

            // Champion box (if final is picked)
            val finalMatch = rounds.find { it.name == RoundName.F }?.matches?.firstOrNull()
            if (finalMatch != null) {
                val championId = picks[finalMatch.player1.id]
                val champion = when (championId) {
                    finalMatch.player1.playerId -> finalMatch.player1
                    finalMatch.player2.playerId -> finalMatch.player2
                    else -> null
                }

                if (champion != null) {
                    Div({
                        style {
                            property("position", "absolute")
                            property("left", "${layout.championX.toInt()}px")
                            property("top", "${(layout.championY - 60).toInt()}px")
                            property("z-index", "2")
                        }
                    }) {
                        ChampionDisplay(
                            playerName = champion.displayName,
                            seed = champion.seed
                        )
                    }
                }
            }
        }
    }
}

// NOTE: HybridBracketTreeContainer has been replaced with InfiniteCanvasBracket
// which provides a much better UX for large brackets. The old modal-based approach
// is no longer used. See InfiniteCanvasBracket.kt for the new implementation.

/**
 * Determine the match node variant based on draw size and round.
 * Larger draws use more compact displays for early rounds.
 */
private fun getVariantForDrawSize(drawSize: Int, round: RoundName): MatchNodeVariant {
    return when {
        drawSize >= 128 && (round == RoundName.R128 || round == RoundName.R64) -> MatchNodeVariant.COMPACT
        drawSize >= 64 && round == RoundName.R64 -> MatchNodeVariant.COMPACT
        else -> MatchNodeVariant.FULL
    }
}

// NOTE: For large draws (64/128), the Tree View now uses InfiniteCanvasBracket
// which shows the entire bracket on a pannable/zoomable canvas instead of
// hiding early rounds behind section modals.

/**
 * Displays the champion at the center of the bracket.
 */
@Composable
private fun ChampionDisplay(
    playerName: String,
    seed: Int?
) {
    Div({
        style {
            backgroundColor(Colors.Secondary)
            borderRadius(12.px)
            padding(16.px, 24.px)
            textAlign("center")
            property("box-shadow", "0 4px 12px rgba(255, 215, 0, 0.4)")
        }
    }) {
        // Trophy emoji
        Div({
            style {
                fontSize(32.px)
                marginBottom(8.px)
            }
        }) {
            Text("\uD83C\uDFC6") // Trophy emoji
        }

        // Champion label
        Div({
            style {
                fontSize(12.px)
                fontWeight(600)
                color(Colors.TextSecondary)
                marginBottom(4.px)
                textTransform("uppercase")
                letterSpacing(1.px)
            }
        }) {
            Text("Champion")
        }

        // Player name
        Div({
            style {
                fontSize(16.px)
                fontWeight(700)
                color(Colors.TextPrimary)
            }
        }) {
            seed?.let {
                Span({
                    style {
                        backgroundColor(Colors.Primary)
                        color(Colors.White)
                        fontSize(11.px)
                        fontWeight(600)
                        padding(2.px, 6.px)
                        borderRadius(4.px)
                        marginRight(8.px)
                    }
                }) {
                    Text("$it")
                }
            }
            Text(playerName)
        }
    }
}

/**
 * Simplified bracket tree for smaller displays or mobile.
 * Shows one half at a time with swipe navigation.
 */
@Composable
fun BracketTreeHalfView(
    rounds: List<BracketRoundEntity>,
    drawSize: Int,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    showTopHalf: Boolean,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit,
    onSwitchHalf: () -> Unit
) {
    Div({
        style {
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Half selector tabs
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                gap(8.px)
                padding(12.px)
                backgroundColor(Colors.Surface)
                borderBottom(1.px, LineStyle.Solid, Colors.Border)
            }
        }) {
            HalfTab(
                label = "Top Half",
                isSelected = showTopHalf,
                onClick = { if (!showTopHalf) onSwitchHalf() }
            )
            HalfTab(
                label = "Bottom Half",
                isSelected = !showTopHalf,
                onClick = { if (showTopHalf) onSwitchHalf() }
            )
        }

        // Bracket content
        Div({
            style {
                flex(1)
                overflow("auto")
            }
        }) {
            // Filter matches for selected half
            val filteredRounds = rounds.map { round ->
                val matchesPerHalf = round.matches.size / 2
                val filteredMatches = if (round.name == RoundName.F) {
                    round.matches // Final is shown in both halves
                } else if (showTopHalf) {
                    round.matches.take(matchesPerHalf)
                } else {
                    round.matches.drop(matchesPerHalf)
                }
                round.copy(matches = filteredMatches)
            }

            BracketTreeContainer(
                rounds = filteredRounds,
                drawSize = drawSize / 2, // Treat as smaller draw for layout
                picks = picks,
                isViewOnly = isViewOnly,
                onSelectWinner = onSelectWinner,
                onShowMatchDetails = onShowMatchDetails,
                // Pass full rounds for pick advancement tracing
                allRoundsForPickCalculation = rounds
            )
        }
    }
}

@Composable
private fun HalfTab(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        classes("half-view-tab")
        if (isSelected) classes("active")
        style {
            padding(8.px, 16.px)
            borderRadius(8.px)
            cursor("pointer")
            backgroundColor(if (isSelected) Colors.Primary else Colors.SurfaceVariant)
            color(if (isSelected) Colors.White else Colors.TextPrimary)
            fontWeight(if (isSelected) 600 else 400)
            fontSize(14.px)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

/**
 * Responsive bracket container that adapts to mobile/desktop.
 *
 * On mobile:
 * - Shows half-view with swipe navigation between halves
 * - Supports pinch-to-zoom
 * - Touch-optimized interactions
 *
 * On desktop:
 * - Shows full bracket tree
 * - Hover states and keyboard navigation
 */
@Composable
fun ResponsiveBracketContainer(
    rounds: List<BracketRoundEntity>,
    drawSize: Int,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit,
    onShowBirdEye: () -> Unit
) {
    val responsive = useResponsive()
    var showTopHalf by remember { mutableStateOf(true) }
    var zoomLevel by remember { mutableStateOf(1.0f) }
    var showZoomIndicator by remember { mutableStateOf(false) }

    // Touch gesture handling
    val touchHandlers = useTouchGestures(
        onGesture = { gesture ->
            when (gesture) {
                is TouchGesture.Swipe -> {
                    when (gesture.direction) {
                        SwipeDirection.LEFT -> showTopHalf = false
                        SwipeDirection.RIGHT -> showTopHalf = true
                        else -> {}
                    }
                }
                is TouchGesture.Pinch -> {
                    zoomLevel = (zoomLevel * gesture.scale.toFloat()).coerceIn(0.5f, 2.0f)
                    showZoomIndicator = true
                }
                is TouchGesture.DoubleTap -> {
                    // Double tap to toggle bird's eye view
                    onShowBirdEye()
                }
                else -> {}
            }
        }
    )

    // Hide zoom indicator after gesture ends
    LaunchedEffect(touchHandlers.state.zoomScale) {
        if (touchHandlers.state.zoomScale == 1.0) {
            kotlinx.coroutines.delay(1000)
            showZoomIndicator = false
        }
    }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            position(Position.Relative)
        }
    }) {
        if (responsive.isMobile) {
            // Mobile view with touch gestures
            MobileBracketView(
                rounds = rounds,
                drawSize = drawSize,
                picks = picks,
                isViewOnly = isViewOnly,
                showTopHalf = showTopHalf,
                zoomLevel = zoomLevel,
                onSelectWinner = onSelectWinner,
                onShowMatchDetails = onShowMatchDetails,
                onSwitchHalf = { showTopHalf = !showTopHalf },
                touchHandlers = touchHandlers
            )

            // Zoom indicator
            if (showZoomIndicator) {
                Div({
                    classes("zoom-indicator", "visible")
                }) {
                    Text("${(zoomLevel * 100).toInt()}%")
                }
            }

            // Floating action button
            MobileFAB(
                onClick = onShowBirdEye,
                icon = "\uD83D\uDDFA\uFE0F"
            )
        } else {
            // Desktop view
            BracketTreeContainer(
                rounds = rounds,
                drawSize = drawSize,
                picks = picks,
                isViewOnly = isViewOnly,
                onSelectWinner = onSelectWinner,
                onShowMatchDetails = onShowMatchDetails
            )
        }
    }
}

/**
 * Mobile-optimized bracket view with swipe navigation.
 */
@Composable
private fun MobileBracketView(
    rounds: List<BracketRoundEntity>,
    drawSize: Int,
    picks: Map<String, String>,
    isViewOnly: Boolean,
    showTopHalf: Boolean,
    zoomLevel: Float,
    onSelectWinner: (positionId: String, winnerId: String) -> Unit,
    onShowMatchDetails: (BracketMatchEntity) -> Unit,
    onSwitchHalf: () -> Unit,
    @Suppress("UNUSED_PARAMETER") touchHandlers: TouchGestureHandlers
) {
    Div({
        style {
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        // Half selector tabs
        Div({
            classes("half-view-tabs")
        }) {
            HalfTab(
                label = "Top Half",
                isSelected = showTopHalf,
                onClick = { if (!showTopHalf) onSwitchHalf() }
            )
            HalfTab(
                label = "Bottom Half",
                isSelected = !showTopHalf,
                onClick = { if (showTopHalf) onSwitchHalf() }
            )
        }

        // Swipe hints
        Div({
            style {
                position(Position.Relative)
                flex(1)
                overflow("hidden")
            }
        }) {
            // Left swipe hint
            if (!showTopHalf) {
                Div({
                    classes("swipe-hint", "left", "visible")
                }) {
                    Text("\u2190") // Left arrow
                }
            }

            // Right swipe hint
            if (showTopHalf) {
                Div({
                    classes("swipe-hint", "right", "visible")
                }) {
                    Text("\u2192") // Right arrow
                }
            }

            // Bracket content with touch handlers
            Div({
                style {
                    width(100.percent)
                    height(100.percent)
                    overflow("auto")
                    property("-webkit-overflow-scrolling", "touch")
                    property("transform", "scale($zoomLevel)")
                    property("transform-origin", "center top")
                }
                // Attach touch handlers
                attr("ontouchstart", "")
                attr("ontouchmove", "")
                attr("ontouchend", "")
            }) {
                // Filter matches for selected half
                val filteredRounds = rounds.map { round ->
                    val matchesPerHalf = round.matches.size / 2
                    val filteredMatches = if (round.name == RoundName.F) {
                        round.matches
                    } else if (showTopHalf) {
                        round.matches.take(maxOf(1, matchesPerHalf))
                    } else {
                        round.matches.drop(matchesPerHalf)
                    }
                    round.copy(matches = filteredMatches)
                }

                BracketTreeContainer(
                    rounds = filteredRounds,
                    drawSize = maxOf(16, drawSize / 2),
                    picks = picks,
                    isViewOnly = isViewOnly,
                    onSelectWinner = onSelectWinner,
                    onShowMatchDetails = onShowMatchDetails,
                    displayMode = BracketDisplayMode.FULL,
                    // Pass full rounds for pick advancement tracing
                    allRoundsForPickCalculation = rounds
                )
            }
        }
    }
}

/**
 * Floating action button for mobile.
 */
@Composable
private fun MobileFAB(
    onClick: () -> Unit,
    icon: String
) {
    Div({
        classes("bracket-fab")
        onClick { onClick() }
        attr("role", "button")
        attr("aria-label", "Bird's eye view")
    }) {
        Text(icon)
    }
}
